import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  searchNotifications
} from '@/shared/services/data/notifications/notifications.data';
import { getUserId } from '@/shared/services/helpers/auth-helper/auth-helper.service';
import type { NotificationSearchPayload } from '@/shared/types/notification.types';

export const notificationsQueryKeys = {
  all: ['notifications'] as const,
  list: (payload: NotificationSearchPayload) =>
    [...notificationsQueryKeys.all, 'list', payload] as const,
  unreadCount: () => [...notificationsQueryKeys.all, 'unread-count'] as const
};

const POLL_MS = 60_000;
/**
 * The notification service stores a single user's notifications across
 * every product they're logged into (partner OTPs, internal-admin push,
 * customer app FCM, etc.). The bell on the admin app should only show
 * notifications addressed to the admin surface — without this filter
 * we leaked unrelated partner-platform OTP logs into the admin bell.
 */
const ADMIN_PLATFORM = 'internal_admin';

export const useNotificationsList = (payload: NotificationSearchPayload = {}) => {
  const userId = getUserId();
  const merged: NotificationSearchPayload = {
    userId,
    platform: ADMIN_PLATFORM,
    limit: 20,
    skip: 0,
    ...payload
  };
  return useQuery({
    queryKey: notificationsQueryKeys.list(merged),
    queryFn: () => searchNotifications(merged),
    enabled: Boolean(userId),
    staleTime: 30_000
  });
};

export const useUnreadNotificationsCount = () =>
  useQuery({
    queryKey: notificationsQueryKeys.unreadCount(),
    queryFn: () => fetchUnreadCount({ platform: ADMIN_PLATFORM }),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 15_000
  });

export const useMarkNotificationRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsQueryKeys.all });
    }
  });
};

export const useMarkAllNotificationsRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsQueryKeys.all });
    }
  });
};
